import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Download, FileText, Users, IndianRupeeIcon, Calendar,
  TrendingUp, Activity, PieChart as PieIcon, ArrowUpRight,
  Filter, ChevronDown, Share2, Printer, Search, Layout, Building2, CheckCircle2,
  Table as TableIcon, Info, ClipboardCheck, AlertCircle, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { cn } from "../ui/utils";
import { Skeleton } from "../ui/skeleton";
import API from '../api/api01';
import { toast } from 'sonner';
import { exportToCSV } from '../utils/exportUtils';

const COLORS = ['#2563eb', '#10b981', '#6366f1', '#f59e0b', '#ef4444'];

const MODULE_TYPES = [
  { id: 'leads', label: 'Lead Tracking', icon: Users, color: 'text-blue-600 dark:text-blue-400' },
  { id: 'social', label: 'Social Manager', icon: Share2, color: 'text-emerald-600 dark:text-emerald-400' },
  { id: 'lms', label: 'LMS Academy', icon: Layout, color: 'text-indigo-600 dark:text-indigo-400' },
  { id: 'payroll', label: 'Finance & HR', icon: IndianRupeeIcon, color: 'text-slate-600 dark:text-slate-400' },
];

interface ReportsProps {
  forcedClientId?: string;
  isSuperadmin?: boolean;
}

function ReportsSkeleton() {
  return (
    <div className="space-y-6 max-w-[1400px] mx-auto p-4 lg:p-10 animate-in fade-in duration-500 bg-background">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-48 rounded-lg" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-32 rounded-lg" />
            <Skeleton className="h-10 w-32 rounded-lg" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-32 w-full rounded-xl" />
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton className="lg:col-span-2 h-[350px] rounded-xl" />
        <Skeleton className="h-[350px] rounded-xl" />
      </div>

      <Card className="border-border bg-card shadow-sm">
        <CardHeader className="border-b border-border/50">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-4 p-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function Reports({ forcedClientId, isSuperadmin }: ReportsProps) {
  const [activeModule, setActiveModule] = useState('leads');
  const [summaryData, setSummaryData] = useState<any>(null);
  const [detailData, setDetailData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Pagination State
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 25;

  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>(forcedClientId || '');

  const [isDarkMode, setIsDarkMode] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on new search
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const isDark = document.documentElement.classList.contains('dark');
    setIsDarkMode(isDark);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDarkMode(document.documentElement.classList.contains('dark'));
        }
      });
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const fetchClients = async () => {
    if (!isSuperadmin) return;
    try {
      const res = await API.get('/superadmin/clients');
      setClients(res.data || []);
    } catch (err) {
      console.error("Failed to fetch clients", err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const params: any = {
        page,
        page_size: pageSize,
      };

      if (isSuperadmin && selectedClientId) {
        params.client_id = selectedClientId;
      }

      if (debouncedSearch) params.search = debouncedSearch;

      const summaryParams: any = {};
      if (isSuperadmin && selectedClientId) {
        summaryParams.client_id = selectedClientId;
      }

      const [summaryRes, detailRes] = await Promise.all([
        API.get('/superadmin/reports/summary', { params: summaryParams }),
        API.get(`/superadmin/reports/detail/${activeModule}`, { params })
      ]);

      setSummaryData(summaryRes?.data || null);
      setDetailData(detailRes?.data?.data || []);
      setTotalPages(detailRes?.data?.total_pages || 1);
      setTotalCount(detailRes?.data?.total || 0);
    } catch (err) {
      console.error("Reports API Error:", err);
      toast.error("Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperadmin) fetchClients();
  }, [isSuperadmin]);

  useEffect(() => {
    fetchData();
  }, [activeModule, selectedClientId, page, debouncedSearch]);

  const handleExportCSV = () => {
    if (detailData.length === 0) return toast.error("No data available to export");
    const filename = `XERO_${activeModule}_Report_${new Date().toISOString().split('T')[0]}.csv`;
    exportToCSV(detailData, filename);
    toast.success(`CSV Export complete`);
  };

  const chartColors = {
    grid: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    axis: isDarkMode ? '#94a3b8' : '#64748b',
    tooltip: isDarkMode ? '#1e293b' : '#ffffff',
    tooltipBorder: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    text: isDarkMode ? '#f8fafc' : '#0f172a'
  };

  if (loading && !summaryData) return <ReportsSkeleton />;

  if (!summaryData && !loading) return (
    <div className="flex flex-col h-[60vh] items-center justify-center space-y-6 text-center">
      <div className="p-4 bg-red-500/10 rounded-full">
        <AlertCircle className="h-10 w-10 text-red-500" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-bold">Data Synchronization Failed</h2>
        <p className="text-muted-foreground max-w-md mx-auto">We encountered an issue while connecting to the reporting services. Please ensure your session is active and try again.</p>
      </div>
      <Button onClick={fetchData} className="gap-2">
        <RefreshCw className="h-4 w-4" />
        Retry Connection
      </Button>
    </div>
  );

  const pieData = activeModule === 'leads' ? (summaryData?.leadsFunnel || []) : (summaryData?.socialROI || []);

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto p-4 lg:p-10 animate-in fade-in duration-500 bg-background">
      {/* Global Print Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @media print {
          @page { size: A4; margin: 15mm; }

          /* Hide sidebar, navbar, and any UI chrome */
          aside,
          nav,
          header,
          [class*="sidebar"],
          [class*="Sidebar"],
          .no-print,
          .no-print * { display: none !important; visibility: hidden !important; }

          /* Reset the page layout so content fills full width */
          body {
            background: white !important;
            color: black !important;
            padding: 0 !important;
            margin: 0 !important;
            font-family: "Inter", sans-serif !important;
          }

          /* Collapse any flex/grid wrappers that include the sidebar */
          body > *,
          #root > *,
          #root > * > * {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }

          .printable-content { display: block !important; width: 100% !important; background: white !important; margin: 0; padding: 0; }
          h1, h2, h3 { color: #000 !important; font-weight: 800 !important; border: none !important; }
          p, td, th { color: #333 !important; font-size: 10pt !important; }
          table { width: 100% !important; border-collapse: collapse !important; margin-top: 10px; }
          th { background: #f0f0f0 !important; color: #000 !important; border: 0.5pt solid #ccc !important; padding: 8px !important; text-align: left !important; font-weight: bold !important; text-transform: uppercase !important; font-size: 8pt !important; }
          td { border: 0.5pt solid #eee !important; padding: 8px !important; }
          .card { border: 0.5pt solid #eee !important; box-shadow: none !important; background: transparent !important; margin-bottom: 15px !important; break-inside: avoid; border-radius: 0 !important; }
          .card-header { padding: 8px 0 !important; border-bottom: 0.5pt solid #eee !important; }
          .card-content { padding: 10px 0 !important; }
          .recharts-responsive-container { width: 100% !important; height: 250px !important; margin-bottom: 20px; }
          .page-break { page-break-before: always; }
          .no-break { break-inside: avoid; }
        }
        .print-only { display: none; }
      `}} />

      <div className="printable-content space-y-8">

        {/* Professional Document Header (Print Only) */}
        <div className="print-only border-b-4 border-slate-900 pb-6 mb-10">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-black text-slate-950 tracking-tighter uppercase">XEROALPHA</h1>
              <p className="text-[10px] font-bold text-slate-500 tracking-[0.2em] uppercase mt-1">Operational Intelligence System</p>
            </div>
            <div className="text-right">
              <h2 className="text-sm font-bold text-slate-900 uppercase">Performance Summary Report</h2>
              <p className="text-[10px] text-slate-500 mt-1">Report Generated on: {new Date().toLocaleDateString('en-GB')}</p>
            </div>
          </div>
        </div>

        {/* Top Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 no-print">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Performance Dashboard
              <Badge variant="outline" className="ml-3 text-[10px] font-bold uppercase py-0 h-5 border-primary/20 bg-primary/5 text-primary">Live Data</Badge>
            </h1>
            <p className="text-muted-foreground text-sm font-medium mt-1">Real-time analytical results for {activeModule.toUpperCase()}</p>
          </div>

          <div className="flex items-center gap-3">
            {isSuperadmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-10 rounded-lg border-border bg-card hover:bg-accent min-w-[200px] flex justify-between text-xs font-bold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-primary" />
                      <span>{clients.find(c => c.id === selectedClientId)?.full_name || 'Global Organization'}</span>
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 opacity-30" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 rounded-xl border-border shadow-2xl">
                  <DropdownMenuItem onClick={() => setSelectedClientId('')} className="font-bold">Consolidated View</DropdownMenuItem>
                  {clients.map(client => (
                    <DropdownMenuItem key={client.id} onClick={() => setSelectedClientId(client.id)}>
                      {client.full_name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <div className="flex gap-2">
              <Button onClick={() => window.print()} className="h-10 px-5 gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-lg shadow-sm shadow-violet-500/20 text-xs font-bold uppercase tracking-widest transition-all active:scale-95">
                <Printer className="h-4 w-4" /> Professional PDF
              </Button>
              <Button variant="outline" onClick={handleExportCSV} className="h-10 px-5 gap-2 border-border bg-card hover:bg-accent rounded-lg text-xs font-bold uppercase tracking-widest">
                <Download className="h-4 w-4" /> Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Executive Summary Block (Print Only) */}
        <div className="print-only border border-slate-200 p-6 bg-slate-50 mb-6">
          <h3 className="text-xs font-extrabold uppercase mb-4 text-slate-500 tracking-widest border-b border-slate-200 pb-2">I. Executive Summary</h3>
          <table className="border-none">
            <tbody className="border-none">
              <tr className="border-none">
                <td className="border-none font-bold p-1">Primary Metric: Conversion Rate</td>
                <td className="border-none text-right font-black p-1">{summaryData?.conversionRate || 0}%</td>
              </tr>
              <tr className="border-none">
                <td className="border-none font-bold p-1">Total Active Entities Recorded</td>
                <td className="border-none text-right font-black p-1">{(summaryData?.summary?.leads || 0).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Dashboard Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 no-break">
          <Card className="border-border bg-card shadow-sm overflow-hidden no-break">
            <div className="h-1 w-full bg-blue-600 no-print" />
            <CardContent className="pt-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Conversion Efficiency</p>
              <div className="flex items-baseline gap-2">
                <h3 className="text-3xl font-bold tracking-tight text-foreground">{summaryData?.conversionRate || 0}%</h3>
                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 no-print flex items-center">
                  <CheckCircle2 className="h-3 w-3 mr-0.5" /> High
                </span>
              </div>
              <Progress value={summaryData?.conversionRate || 0} className="h-1 mt-4 no-print bg-muted" />
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-sm overflow-hidden no-break">
            <div className="h-1 w-full bg-emerald-600 no-print" />
            <CardContent className="pt-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Total Unit Intake</p>
              <h3 className="text-3xl font-bold tracking-tight text-foreground">{(summaryData?.summary?.leads || 0).toLocaleString()}</h3>
              <p className="text-[10px] font-medium text-muted-foreground mt-2">Entities Recorded</p>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-sm lg:col-span-2 overflow-hidden no-break">
            <div className="h-1 w-full bg-primary no-print" />
            <CardHeader className="py-4 px-6 flex flex-row items-center justify-between border-b border-border/50">
              <CardTitle className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Internal Contributor Rankings</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground/30 no-print" />
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {(summaryData?.counselors || []).map((c: any, i: number) => (
                  <div key={i} className="space-y-1">
                    <p className="text-[10px] font-bold text-foreground truncate">{c.name}</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 bg-muted rounded-full">
                        <div className="h-full bg-primary rounded-full shadow-[0_0_8px_rgba(37,99,235,0.4)]" style={{ width: `${(c.value / (summaryData?.summary?.leads || 1)) * 100}%` }} />
                      </div>
                      <span className="text-[9px] font-bold text-muted-foreground">{c.value}Units</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Module Switcher (Interactive Only) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 no-print">
          {MODULE_TYPES.map((mod) => (
            <div key={mod.id}
              onClick={() => setActiveModule(mod.id)}
              className={cn(
                "group relative cursor-pointer p-5 rounded-xl border transition-all duration-300",
                mod.id === activeModule
                  ? "bg-primary border-primary shadow-lg shadow-primary/20 translate-y-[-2px]"
                  : "bg-card border-border hover:border-primary/30"
              )}>
              <div className="flex justify-between items-start">
                <div className={cn("p-2 rounded-lg", mod.id === activeModule ? "bg-white/20" : "bg-muted")}>
                  <mod.icon size={18} className={mod.id === activeModule ? "text-white" : mod.color} />
                </div>
                {mod.id === activeModule && <CheckCircle2 className="h-4 w-4 text-white animate-in zoom-in" />}
              </div>
              <div className="mt-4">
                <p className={cn("text-[10px] font-bold uppercase tracking-widest font-bold", mod.id === activeModule ? "text-white/70" : "text-muted-foreground")}>{mod.label}</p>
                <h4 className={cn("text-xl font-bold tracking-tight mt-1", mod.id === activeModule ? "text-white" : "text-foreground")}>
                  {mod.id === 'payroll'
                    ? `₹${(summaryData?.summary?.[mod.id] || 0).toLocaleString()}`
                    : (summaryData?.summary?.[mod.id] || 0).toLocaleString()}
                </h4>
              </div>
            </div>
          ))}
        </div>

        {/* Analytics Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-border bg-card shadow-sm no-break">
            <CardHeader className="border-b border-border/50">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-sm font-bold text-foreground">Trend Intensity Analysis</CardTitle>
                  <CardDescription className="text-[10px] text-muted-foreground">Historical volume tracking for current fiscal period</CardDescription>
                </div>
                <Activity className="h-4 w-4 text-muted-foreground/30 no-print" />
              </div>
            </CardHeader>
            <CardContent className="pt-8 min-h-[300px]">
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={summaryData?.monthlyTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.grid} />
                  <XAxis dataKey="month" stroke={chartColors.axis} fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke={chartColors.axis} fontSize={10} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      fontSize: '11px',
                      backgroundColor: chartColors.tooltip,
                      borderColor: chartColors.tooltipBorder,
                    }}
                  />
                  <Area type="monotone" dataKey="leads" name="Active Intake" stroke="#2563eb" strokeWidth={2} fill="rgba(37, 99, 235, 0.05)" />
                  <Area type="monotone" dataKey="conversions" name="Successful Conversion" stroke="#10b981" strokeWidth={2} fill="transparent" strokeDasharray="5 5" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-sm no-break">
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-sm font-bold text-foreground">Operational Distribution</CardTitle>
              <CardDescription className="text-[10px] text-muted-foreground">Percentage breakdown by current status</CardDescription>
            </CardHeader>
            <CardContent className="pt-4 min-h-[250px]">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={75}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {pieData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Data Records Table */}
        <Card className="border-border bg-card shadow-sm no-break">
          <CardHeader className="border-b border-border/50 flex flex-row items-center justify-between no-break">
            <div className="flex items-center gap-3">
              <TableIcon className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-bold text-foreground uppercase tracking-tight">Technical Data Records Index</CardTitle>
            </div>
            <div className="relative w-72 no-print">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
              <Input
                placeholder="Search by name or status..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8 text-xs rounded-lg border-border bg-muted/20 text-foreground"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs bg-transparent">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground font-bold uppercase tracking-widest border-b border-border/50">
                    <th className="px-6 py-4">Participant Detail</th>
                    <th className="px-6 py-4">Reference Identity</th>
                    <th className="px-6 py-4">College / University</th>
                    <th className="px-6 py-4">Execution Status</th>
                    <th className="px-6 py-4">Entry Date</th>
                    <th className="px-6 py-4 text-right no-print">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {detailData.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/10 transition-colors group">
                      <td className="px-6 py-3">
                        <div className="flex flex-col">
                          <span className="font-bold text-foreground text-xs">{item.name}</span>
                          <span className="text-[10px] text-muted-foreground italic font-medium">{item.email || item.period || 'Internal Ref'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-3 font-mono text-[9px] text-muted-foreground/70 dark:text-muted-foreground/40 uppercase">
                        #{item.id?.slice(0, 8) || 'IDENT'}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground">
                          <Building2 className="h-3 w-3 opacity-50" />
                          {item.campus}
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <Badge variant="outline" className={cn(
                          "text-[8px] font-black uppercase h-5 px-2",
                          ['Converted', 'Paid', 'Active', 'enrolled'].includes(item.status)
                            ? 'text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-500/10'
                            : 'text-slate-500 dark:text-slate-400 border-border bg-slate-100/50 dark:bg-slate-800/50'
                        )}>
                          {item.status || item.role}
                        </Badge>
                      </td>
                      <td className="px-6 py-3 text-muted-foreground font-bold text-[10px]">
                        {item.date ? new Date(item.date).toLocaleDateString('en-GB') : 'N/A'}
                      </td>
                      <td className="px-6 py-3 text-right no-print">
                        <ArrowUpRight size={14} className="text-muted-foreground/20 group-hover:text-primary transition-colors ml-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {detailData.length === 0 && !loading && (
                <div className="p-12 text-center text-muted-foreground uppercase text-[10px] font-bold tracking-widest opacity-30 italic">
                  No records found matching your current filter set
                </div>
              )}
              {loading && (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mt-4 text-muted-foreground animate-pulse">Synchronizing Data Node...</p>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            <div className="px-6 py-4 bg-muted/20 border-t border-border/50 flex flex-col md:flex-row items-center justify-between gap-4 no-print">
              <div className="flex items-center gap-4">
                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                  Showing Page <span className="text-foreground">{page}</span> of <span className="text-foreground">{totalPages}</span>
                </span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/30 ml-2 border-l border-border/50 pl-4">
                  Total Records: <span className="text-foreground">{totalCount.toLocaleString()}</span>
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="h-8 px-4 text-[10px] font-bold uppercase tracking-widest border-border hover:bg-background"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  className="h-8 px-4 text-[10px] font-bold uppercase tracking-widest border-border hover:bg-background"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
